import _ from 'lodash';
import { defineStore, storeToRefs } from 'pinia';
import { EventTemplates } from '@or-sdk/hitl';

import { ISessionEvent } from '@/types';
import { HITL } from '@/api';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';
import { sortEventTemplates } from '@/apps/app-settings/modules/event-templates/constants';

export const useEventTemplatesStore = defineStore('HITL-Event-Templates', {
  state: () => {
    return {
      isLoadingEventTemplates: false,
      eventTemplatesById: {} as EventTemplatesById,
    };
  },
  getters: {
    eventTemplates: state => {
      const templatesArray = Object.values(state.eventTemplatesById);
      return sortEventTemplates(templatesArray);
    },

    getEventTemplateById: state => {
      return (templateId: string) => {
        return state.eventTemplatesById[templateId];
      };
    },
  },
});

export const useEventTemplatesActions = () => {
  const store = useEventTemplatesStore();
  /** Agent UI should provide a lazy caching method like local storage */
  const initializeEventTemplates = async (options: {
    withCache?: <T>(request: () => Promise<T>) => Promise<T>;
  } = {}) => {
    try {
      const { withCache = request => request() } = options;
      store.isLoadingEventTemplates = true;
      const response = await withCache(() => {
        return HITL.eventTemplates
          .getEventTemplates() as Promise<EventTemplates.GetEventTemplatesResponse>;
      });

      const onlyActive = response.data.filter(item => item.isActive);
      store.eventTemplatesById = _.keyBy(onlyActive, 'templateId');
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    } finally {
      store.isLoadingEventTemplates = false;
    }
  };

  const getEventTemplate = (event: ISessionEvent) => {
    const eventTemplate = store.eventTemplates.find(item => {
      return checkRule(event, item.rule);
    });

    if (eventTemplate) {
      const { isVisible, templateId } = eventTemplate;
      return {
        is: 'EventTemplate',
        visibility: isVisible,
        templateId,
      };
    } else {
      return { is: event.event };
    }
  };

  /** TODO: This is very unsafe! */
  const checkRule = (event: ISessionEvent, rule: string) => {
    const args = Object.keys(event);
    const values = Object.values(event);
    try {
      const code = `try { return ${rule}; } catch (error) { return false; }`;
      const checker = new Function(...args, code);
      return checker(...values);
    } catch (error) {
      console.debug('Rule Processing Error', error);
      return false;
    }
  };

  return {
    initializeEventTemplates,
    getEventTemplate,
  };
};

export const useEventTemplates = () => {
  return {
    ...useEventTemplatesActions(),
    ...storeToRefs(useEventTemplatesStore()),
  };
};

export type EventTemplatesById = {
  [templateId: string]: EventTemplates.EventTemplate;
};
